import React, { useEffect, useState } from "react";
import { buildPolicyVersionDetailsRoute } from "../../../../routes/AdminRoutes";
import { useDispatch, useSelector } from "react-redux";
import AdminPageLayout from "../../../../components/FullPageLayout/AdminPageLayout";
import { push } from "connected-react-router";
import Breadcrumb from "../../../../components/Breadcrumb/Breadcrumb";
import { fetchCourseVersionData } from "../../../../redux/actions/contentCoursesActions";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { usePolicyDocuments } from "../../../../hooks/policyDocuments";
import Skeleton from "../../../../components/Skeleton/Skeleton";
import { Box, Typography } from "@mui/material";
import PolicyEditor from "../../../../components/PolicyBuilder/PolicyEditor";
import AddItemFromRepository from "../../Courses/CourseBuilder/AddItemFromRepository";
import AddCourse from "../../Courses/CourseBuilder/AddCourse";
import AddNewContentForm from "../../../../components/Repository/AddNewContentForm";
import Modal from "../../../../components/Modal";
import useUser from "../../../../hooks/useUser";
import { enqueueErrorSnackbar } from "../../../../redux/actions/snackbarActions";
import { useTranslation } from "react-i18next";
import { useContentRepositoryAttachables } from "../../../../hooks/contentRepositoryAttachables";
import { usePolicyEditor } from "../../../../hooks/usePolicyEditor";
import TrainingLink from "./TrainingLink";

const selector = makeGetEntities();

const AddFileModal = ({ open, onClose, onSubmit }) => {
  const { t } = useTranslation();
  return (
    <Modal
      showCloseIcon
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {t("policyEditor.addFileModal.title")}
        </Typography>
      }
      open={open}
      onClose={onClose}
      dialogContent={
        <AddNewContentForm
          onCancel={onClose}
          onSubmit={onSubmit}
          allowQuizImport={false}
          buttonTypes={["submit"]}
          showEditorBehaviorField
        />
      }
      maxWidth="lg"
      fullWidth
      fillHeight
    />
  );
};

const PolicyEditorTitle = ({ policyName }) => {
  return (
    <Breadcrumb
      routes={["Policies", policyName, "Manage Version", "Policy Editor"]}
    />
  );
};

const PolicyEditorView = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { versionId, policyId, policyDocumentId } = useParams();
  const [editor, setEditor] = useState(null);
  const [addRepositoryItemModalOpen, setAddRepositoryItemModalOpen] = useState(
    false
  );
  const [addFileModalOpen, setAddFileModalOpen] = useState(false);
  const [linkTrainingModalOpen, setLinkTrainingModalOpen] = useState(false);
  const [policyDocument, setPolicyDocument] = useState(null);
  const {
    destroy: destroyContentRepositoryAttachable
  } = useContentRepositoryAttachables();

  const {
    createRepositoryItemLinks,
    createTrainingLinks,
    onAddRepositoryItem,
    isCreatingTrainingLinks
  } = usePolicyEditor(editor, {
    contentRepositoryItemId: policyDocument?.item?.id
  });
  const currentUser = useUser();
  const {
    findPolicyDocument,
    updatePolicyDocument,
    isFetching
  } = usePolicyDocuments();

  const { versionData } = useSelector(({ contentCoursesState }) => ({
    versionData: selector(contentCoursesState, {
      type: "version",
      id: versionId
    })
  }));

  useEffect(() => {
    dispatch(fetchCourseVersionData(policyId, versionId));
    findPolicyDocument({ policyDocumentId, include: "item" }).then(
      setPolicyDocument
    );
  }, []);

  useEffect(() => {
    if (editor) {
      editor.on("addRepositoryItem", () => {
        setAddRepositoryItemModalOpen(true);
      });
      editor.on("addFile", () => {
        setAddFileModalOpen(true);
      });
      editor.on("linkTraining", () => {
        setLinkTrainingModalOpen(true);
      });
      editor.on("removeAttachable", (_e, contentRepositoryAttachableId) => {
        destroyContentRepositoryAttachable(contentRepositoryAttachableId);
      });
    }
  }, [editor]);

  const handleGoBackToPolicyVersionDetails = () => {
    dispatch(push(buildPolicyVersionDetailsRoute(policyId, versionId)));
  };

  const handleSaveEditorContent = content => {
    updatePolicyDocument({
      content,
      id: policyDocumentId,
      versionId: versionId
    });
  };

  const handleAddRepositoryItems = items => {
    createRepositoryItemLinks(items).then(() => {
      setAddRepositoryItemModalOpen(false);
    });
  };

  const handleAddFile = newFileData => {
    onAddRepositoryItem({
      ...newFileData,
      creatorId: currentUser.id,
      libraryId: policyDocument.item.library.id
    })
      .then(() => {
        setAddFileModalOpen(false);
      })
      .catch(() => {
        dispatch(
          enqueueErrorSnackbar({
            message: t("policyEditor.fileUploadErrorMessage")
          })
        );
      });
  };

  const handleLinkTraining = courseIds => {
    createTrainingLinks(courseIds)
      .then(() => {
        setLinkTrainingModalOpen(false);
      })
      .catch(error => {
        console.log("Could not link training", error);
        dispatch(
          enqueueErrorSnackbar({
            message: t("policyEditor.linkTrainingErrorMessage")
          })
        );
      });
  };

  return (
    <AdminPageLayout
      title={
        <PolicyEditorTitle
          policyName={versionData ? versionData.name : ""}
          releaseVersion={versionData ? versionData.release : ""}
        />
      }
      onGoBackClick={() =>
        handleGoBackToPolicyVersionDetails(versionData ? versionData.type : "")
      }
    >
      <Box mt={3}>
        <Skeleton height={240} loading={isFetching}>
          <PolicyEditor
            onSave={handleSaveEditorContent}
            initialData={policyDocument?.content}
            onReady={setEditor}
            trainingLinkComponent={TrainingLink}
          />
        </Skeleton>
      </Box>
      <AddItemFromRepository
        open={addRepositoryItemModalOpen}
        onClose={() => setAddRepositoryItemModalOpen(false)}
        onAddItems={handleAddRepositoryItems}
        courseOrPolicy={"policy"}
      />
      <AddFileModal
        open={addFileModalOpen}
        onClose={() => setAddFileModalOpen(false)}
        onSubmit={handleAddFile}
      />
      {linkTrainingModalOpen && (
        <AddCourse
          open={linkTrainingModalOpen}
          onClose={() => setLinkTrainingModalOpen(false)}
          onAddCourses={handleLinkTraining}
          includePolicies={true}
          isSubmitting={isCreatingTrainingLinks}
          excludeChildTrainingObjects
          hideTotalActivitiesColumn
          excludedTrainingObjectIds={[policyId]}
        />
      )}
    </AdminPageLayout>
  );
};

export default PolicyEditorView;
